/**
 * Come si leggono i numeri della console.
 *
 * [sc-48497 / task 48534] Le tre pagine della console mostrano le stesse cose
 * — una data, uno stato, i giorni che restano — e se ognuna se le formattasse
 * per conto proprio direbbero la stessa cosa in tre modi. Sono funzioni pure:
 * si provano da sole, senza montare una pagina.
 */

/** Come si chiamano gli stati del tenant quando li legge una persona. */
const ETICHETTE = {
  prova: "In prova",
  attivo: "Attiva",
  scaduto: "Prova scaduta",
  // [sc-48497 / task 48532] Non e' uno stato della colonna `status`: e' il
  // fatto che i dati non ci sono piu', che dal punto di vista di chi guarda la
  // console conta piu' dello stato della prova finita mesi fa.
  cancellato: "Dati cancellati",
};

export function platformStatusLabel(status) {
  return ETICHETTE[status] || status || "—";
}

/**
 * Come sta l'azienda, per chi la guarda dalla console.
 *
 * [sc-48497 / task 48532] La cancellazione vince sullo stato: un'azienda coi
 * dati cancellati resta `scaduto` in tabella, ma mostrarla come «Prova
 * scaduta» con tutti zeri accanto la fa sembrare una che non ha mai caricato
 * niente. Sono due storie diverse e la console deve distinguerle.
 */
export function platformCompanyState(company) {
  if (company?.dataDeletedAt) return "cancellato";
  return company?.status ?? null;
}

/** Data in forma breve italiana; il fuso lo mette il browser di chi guarda. */
export function formatPlatformDate(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Data e ora: serve al registro, dove due sguardi possono cadere lo stesso giorno. */
export function formatPlatformDateTime(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * A che punto è la prova.
 *
 * Un'azienda attiva non ha più una prova da raccontare: dire «0 giorni» a chi
 * ha sottoscritto sarebbe un modo di sembrare rotti.
 */
export function platformTrialLabel(company) {
  if (company?.status === "attivo") return "—";

  const giorni = company?.daysLeft;
  if (giorni === null || giorni === undefined) return "—";
  if (giorni < 0) return `finita da ${Math.abs(giorni)} g`;
  if (giorni === 0) return "ultimo giorno";
  return `${giorni} g`;
}

/**
 * Quando è entrata l'ultima volta.
 *
 * [sc-48497 / task 48534] «Mai» non è un buco da riempire con un trattino: è
 * la risposta più interessante che questa colonna possa dare — un'azienda
 * registrata che non è mai entrata.
 */
export function platformAccessLabel(iso, company) {
  // [sc-48497 / task 48532] Con i dati spariscono anche gli operatori, e
  // l'ultimo accesso viveva su di loro. Dire «mai entrata» sarebbe inventarsi
  // un fatto al posto di un'informazione che non abbiamo piu': quell'azienda
  // era entrata eccome, e non lo sappiamo piu' dire.
  if (company?.dataDeletedAt) return "cancellato con i dati";
  if (!iso) return "mai entrata";
  return formatPlatformDateTime(iso);
}

/**
 * Da quando l'azienda è attiva.
 *
 * [sc-48497 / task 48570] La data non è una colonna: è quella della riga di
 * attivazione nel registro. Non ce l'hanno le aziende che c'erano prima della
 * registrazione: la migrazione della 48526 le ha rese attive perché non erano
 * prove, e nessuno ha premuto niente. Lo si dice come un fatto — non con un
 * trattino, che sembrerebbe un dato perso.
 */
export function platformActivationLabel(company) {
  if (!company?.activatedAt) return "da prima della console";
  return formatPlatformDate(company.activatedAt);
}

/**
 * [sc-48692] Stato dell'invito di un operatore, in parole. `invito` è quello
 * che manda l'API: `null` per chi non è nato da un invito.
 */
export function platformInviteLabel(invito) {
  if (!invito) return "—";
  if (invito.stato === "attivato") {
    return `attivato il ${formatPlatformDateTime(invito.il)}`;
  }
  if (invito.stato === "in_attesa") {
    return `in attesa, scade il ${formatPlatformDateTime(invito.scade)}`;
  }
  return "scaduto";
}

/**
 * [sc-48692] Si rimanda solo un invito non ancora usato, a un operatore non
 * archiviato. Lo stesso controllo lo fa l'API: qui serve a non mostrare un
 * pulsante che verrebbe rifiutato.
 */
export function platformInviteResendable(operatore) {
  return (
    !operatore?.isArchived &&
    ["in_attesa", "scaduto"].includes(operatore?.invito?.stato)
  );
}

/**
 * [sc-48351] Il codice d'invito come si detta: due gruppi da tre cifre.
 */
export function platformInviteCode(codice) {
  const cifre = String(codice ?? "").replace(/\D/g, "");
  return cifre.length === 6 ? `${cifre.slice(0, 3)} ${cifre.slice(3)}` : cifre;
}

/**
 * [sc-48351] Esito dell'avviso per email dopo il passaggio a PLUS. Il piano
 * è cambiato comunque: qui si dice solo chi lo sa già.
 */
export function platformPlanNotice(avviso) {
  const { avvisati = 0, daAvvisare = 0 } = avviso || {};
  if (daAvvisare === 0) {
    return "Passata a PLUS. Nell'azienda non c'è un amministratore attivo da avvisare per email.";
  }
  if (avvisati === daAvvisare) {
    return daAvvisare === 1
      ? "Passata a PLUS. L'amministratore dell'azienda è stato avvisato per email."
      : `Passata a PLUS. I ${daAvvisare} amministratori dell'azienda sono stati avvisati per email.`;
  }
  return `Passata a PLUS, ma l'avviso per email è partito verso ${avvisati} amministratori su ${daAvvisare}: avvisa gli altri tu.`;
}
